import React from 'react';

/**
 * KD-3581 §3, level 1 — the frame wrapping every day of one experience.
 *
 * Slots rather than data: the app builds the header, the day cards and the
 * global services band, this component only fixes where they sit. Global
 * services close the frame, after the last day, because a global option applies
 * to the whole experience and must not look attached to a day (§2).
 *
 * The frame is the one tinted surface of the timeline (#FAFBFD): the day cards
 * and the header sit on it in white, which is what makes a multi-day experience
 * read as a single block. Its own geometry — 16px radius, 14px inner gutter,
 * 12px between children — comes from the mockup.
 *
 * `notice` is the removal-undo band (§7). It stays inside the frame so it reads
 * as being about this experience, and the app unmounts the whole frame with it
 * once the undo window has passed.
 */
type TripExperienceFrameProps = Omit<
  React.HTMLAttributes<HTMLElement>,
  'className'
> & {
  notice?: React.ReactNode;
  header?: React.ReactNode;
  /** The day cards of the experience, in day order. */
  children?: React.ReactNode;
  globalServices?: React.ReactNode;
};

const TripExperienceFrame = React.forwardRef<
  HTMLElement,
  TripExperienceFrameProps
>(({ notice, header, children, globalServices, ...props }, ref) => (
  <section
    ref={ref}
    className="mb-6 overflow-hidden rounded-2xl border border-solid border-[#DFE5EE] bg-[#FAFBFD] pb-3.5"
    {...props}
  >
    {notice && <div className="px-3.5 pt-3">{notice}</div>}

    {header}

    <div className="flex flex-col gap-3 px-3.5 pt-3">
      {children}
      {globalServices}
    </div>
  </section>
));

TripExperienceFrame.displayName = 'TripExperienceFrame';

export default TripExperienceFrame;
